import React, { useState, useEffect, Fragment } from 'react';
import { useSelector } from 'react-redux';
import { Input } from 'reactstrap';
import moment from 'moment';

import * as actions from './actions';
import { Button } from 'components/Button/RoundButton';
import { Container, Wrapper } from 'components/Layout/Layout';
import { Header, HeadingContainer } from 'components/PageTitleHeader/styles';
import { InputAndSearchSection, DateLabel, InputSection } from './styles';
import {
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableHeading,
  TableData,
  SortArrowImage,
} from 'components/CMSRankList/styles';
import { useActions } from 'hooks/useActions';
import { Images } from 'assets/images';

export default React.memo(() => {
  const [items, setItems] = useState([]);
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const [sortBy, setSortBy] = useState('name');
  const [isAscending, setIsAscending] = useState(false);

  const [minDate, setMinDate] = useState('');
  const [maxDate, setMaxDate] = useState('');
  const [companyName, setCompanyName] = useState('');

  const { getStatistics } = useActions(actions);

  let { loading, statistics } = useSelector((state) => state.StatisticsPageReducer);

  useEffect(() => {
    const date = new Date();
    const year = date.getFullYear();
    const month = date.getMonth();
    let minimumDate = moment(new Date(year, month, 1)).format('YYYY-MM-DD');
    let maximumDate = moment(new Date()).format('YYYY-MM-DD');
    setMinDate(minimumDate);
    setMaxDate(maximumDate);
    getStatistics({
      minDate: minimumDate,
      maxDate: maximumDate,
      companyName: '',
    });
  }, [getStatistics]);

  useEffect(() => {
    setItems(statistics);
  }, [statistics]);

  const sort = (array, field, isAscending) => {
    let sortableItems = [...array];
    return sortableItems.sort((a, b) => {
      let x, y;
      if (field === 'company') {
        x = a['name'];
        y = b['name'];
      } else if (field === 'creditsUsed') {
        x = a['payment']['creditsUsed'] || 0;
        y = b['payment']['creditsUsed'] || 0;
      } else if (field === 'creditsBought') {
        x = a['payment']['creditsBought'] || 0;
        y = b['payment']['creditsBought'] || 0;
      } else {
        x = a['customerLog']['totalLogin'] || 0;
        y = b['customerLog']['totalLogin'] || 0;
      }
      if (x < y) {
        return isAscending ? -1 : 1;
      }
      if (x > y) {
        return isAscending ? 1 : -1;
      }
      return 0;
    });
  };

  const sortListBy = (field) => {
    let updatedList = sort(statistics, field, field !== sortBy ? false : isAscending);
    setItems(updatedList);
    if (field !== sortBy) {
      setIsAscending(true);
    } else {
      setIsAscending(!isAscending);
    }
    setSortBy(field);
  };

  const setTodaysDate = () => {
    let currentDate = moment(new Date()).format('YYYY-MM-DD');
    setMinDate(currentDate);
    setMaxDate(currentDate);
    getStatistics({
      minDate: currentDate,
      maxDate: currentDate,
      companyName,
    });
  };

  const renderTopSection = () => (
    <InputAndSearchSection>
      <InputSection>
        <DateLabel>Start Date: </DateLabel>
        <Input type="date" value={minDate} onChange={(e) => setMinDate(e.target.value)} />
      </InputSection>
      <InputSection>
        <DateLabel>End Date: </DateLabel>
        <Input type="date" value={maxDate} onChange={(e) => setMaxDate(e.target.value)} />
      </InputSection>
      <InputSection>
        <Input
          type="text"
          placeholder="Company name"
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
        />
      </InputSection>
      <InputSection>
        <Button
          primaryBlue
          primaryGreenBorder
          small
          removeMargin
          onClick={() => getStatistics({ minDate, maxDate, companyName })}
        >
          Search
        </Button>
        <Button primaryBlue primaryGreenBorder small removeMargin onClick={() => setTodaysDate()}>
          Today
        </Button>
      </InputSection>
    </InputAndSearchSection>
  );

  const renderTableHeading = () => (
    <TableRow isHeading>
      <TableHeading>
        Company
        <SortArrowImage
          onClick={() => sortListBy('company')}
          alt="arrow"
          src={sortBy === 'company' && isAscending ? Images.upArrow : Images.downArrow}
        />
      </TableHeading>
      <TableHeading>
        Credits Used
        <SortArrowImage
          onClick={() => sortListBy('creditsUsed')}
          alt="arrow"
          src={sortBy === 'creditsUsed' && isAscending ? Images.upArrow : Images.downArrow}
        />
      </TableHeading>
      <TableHeading>
        Credits Bought{' '}
        <SortArrowImage
          onClick={() => sortListBy('creditsBought')}
          alt="arrow"
          src={sortBy === 'creditsBought' && isAscending ? Images.upArrow : Images.downArrow}
        />
      </TableHeading>
      <TableHeading>
        Login
        <SortArrowImage
          onClick={() => sortListBy('login')}
          alt="arrow"
          src={sortBy === 'login' && isAscending ? Images.upArrow : Images.downArrow}
        />
      </TableHeading>
    </TableRow>
  );

  const renderUserDetailsTable = (userDetails) =>
    userDetails && (
      <Table>
        <TableHead>
          <TableRow isHeading>
            <TableHeading>Name</TableHeading>
            <TableHeading>Date</TableHeading>
          </TableRow>
        </TableHead>
        <TableBody>
          {userDetails.map((item, index) => {
            return (
              <TableRow key={index}>
                <TableData>{item.name}</TableData>
                <TableData>{moment(item.lastActive).format('DD/MM/YYYY')}</TableData>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );

  const renderTableBody = () =>
    items.map((item, index) => (
      <Fragment key={index}>
        <TableRow>
          <TableData>{item.name}</TableData>
          <TableData>{item.payment.creditsUsed ? item.payment.creditsUsed : 0}</TableData>
          <TableData>{item.payment.creditsBought ? item.payment.creditsBought : 0}</TableData>
          <TableData>
            {item.customerLog._id ? item.customerLog.totalLogin : 0}{' '}
            {item.customerLog.totalLogin > 0 && (
              <i
                style={{ cursor: 'pointer' }}
                className={`pl-1 fa ${index} ${
                  isCollapsed ? 'fa-plus' : activeIndex === index ? 'fa-minus' : 'fa-plus'
                }`}
                onClick={() => {
                  setActiveIndex(index);
                  setIsCollapsed(!isCollapsed);
                }}
              />
            )}
          </TableData>
        </TableRow>
        {!isCollapsed && activeIndex === index && (
          <TableRow>
            <TableData colSpan="4">{renderUserDetailsTable(item.customerLog.details)}</TableData>
          </TableRow>
        )}
      </Fragment>
    ));

  return (
    <Wrapper padding="60px 0 0 0" minHeight="100vh">
      <Container column>
        <HeadingContainer>
          <Header>Company Statistics</Header>
        </HeadingContainer>
        {renderTopSection()}
        {loading && 'Loading'}
        {!loading && !items.length && 'No data found'}
        {!loading && !!items.length && (
          <Table>
            <TableHead>{renderTableHeading()}</TableHead>
            <TableBody>{renderTableBody()}</TableBody>
          </Table>
        )}
      </Container>
    </Wrapper>
  );
});
